import React, { useState, useCallback, useMemo, lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import * as Sentry from "@sentry/react";
import "./AnimeDetails.css";

// Core components (always loaded)
import LoadingSpinner from "../components/shared/LoadingSpinner";
import ErrorMessage from "../components/shared/ErrorMessage";
import MediaTabs from "../components/shared/MediaTabs";
import MediaHero from "../components/mediaDetails/MediaHero";
import MediaPoster from "../components/mediaDetails/MediaPoster";
import MediaHeader from "../components/mediaDetails/MediaHeader";
import StatsGrid from "../components/mediaDetails/StatsGrid";

// Lazy-loaded tab content components
const AnimeAbout = lazy(() => import("../components/animeDetails/AnimeAbout"));
const AnimeTrailer = lazy(() => import("../components/animeDetails/AnimeTrailer"));
const AnimeProduction = lazy(() => import("../components/animeDetails/AnimeProduction"));

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
const placeholderBackdrop = [process.env.REACT_APP_PLACEHOLDER_BACKDROP || 'https://res.cloudinary.com/dpz6ivjry/image/upload/v1741666344/740446_zkrklo.jpg'];

// Move formatDate outside component
const formatDate = (dateString) => {
  if (!dateString) return "Present";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Tab configuration
const tabs = [
  { id: 'about', label: 'About' },
  { id: 'trailer', label: 'Trailer' },
  { id: 'production', label: 'Production' }
];

// Create the base component
const AnimeDetailsComponent = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("about");

  // Use React Query with selective field retrieval
  const { data: anime, isLoading, error } = useQuery({
    queryKey: ['anime', id],
    queryFn: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/anime/${id}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch anime: ${response.status}`);
        }
        return response.json();
      } catch (error) {
        // Basic error capture with Sentry
        Sentry.captureException(error, {
          tags: {
            component: 'AnimeDetails',
            operation: 'fetch'
          },
          extra: {
            animeId: id
          }
        });
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Memoize backdrops
  const backdrops = useMemo(() => {
    if (!anime) return placeholderBackdrop;
    return (anime.backdrops && Array.isArray(anime.backdrops) && anime.backdrops.length > 0)
      ? anime.backdrops
      : placeholderBackdrop;
  }, [anime]);

  // Memoize stats configuration
  const statsConfig = useMemo(() => {
    if (!anime) return [];
    return [
      { label: "Score", value: anime.score || 'N/A' },
      { label: "Scored by", value: anime.scored_by?.toLocaleString() || 'N/A' },
      { label: "Rank", value: `#${anime.rank?.toLocaleString() || 'N/A'}` },
      { label: "Popularity", value: `#${anime.popularity?.toLocaleString() || 'N/A'}` },
      { label: "Members", value: anime.members?.toLocaleString() || 'N/A' },
      { label: "Favorites", value: anime.favorites?.toLocaleString() || 'N/A' },
      { label: "MAL ID", value: anime.mal_id || 'N/A' }
    ];
  }, [anime]);
  
  // Memoize poster image URL to prevent unnecessary re-renders
  const posterImageUrl = useMemo(() => {
    return anime?.images?.webp?.large_image_url || anime?.images?.jpg?.large_image_url || '';
  }, [anime]);
  
  const handleStatusChange = useCallback((newStatus) => {
    // Track user actions with breadcrumbs
    Sentry.addBreadcrumb({
      category: 'user.action',
      message: 'Updated anime status',
      level: 'info',
      data: {
        animeId: id,
        newStatus
      }
    });
    
    try {
      // Implementation of status change
      // (Your existing or future implementation)
    } catch (error) {
      // Capture any errors
      Sentry.captureException(error, {
        tags: {
          component: 'AnimeDetails',
          operation: 'statusChange'
        },
        extra: {
          animeId: id,
          newStatus
        }
      });
    }
  }, [id]);

  // Tab change handler (memoized)
  const handleTabChange = useCallback((tabId) => {
    // Track tab changes as user interactions
    Sentry.addBreadcrumb({
      category: 'ui.interaction',
      message: `Changed tab to ${tabId}`,
      level: 'info'
    });
    
    setActiveTab(tabId);
  }, []);

  // Loading state
  if (isLoading) return <LoadingSpinner />;
  
  // Error state
  if (error) {
    // We've already captured the error in the queryFn
    return <ErrorMessage message={error.message} />;
  }

  return (
    <div className="anime-details">
      {/* Hero Section */}
      <MediaHero backdrops={backdrops}>
        <div className="hero-content">
          <MediaPoster 
            imageUrl={posterImageUrl}
            itemId={anime.id}
            itemType="anime"
            totalEpisodes={anime.episodes}
            onStatusChange={handleStatusChange}
            alt={anime.title}
          />
          
          <div className="info">
            <MediaHeader 
              title={anime.title}
              englishTitle={anime.title_english}
              japaneseTitle={anime.title_japanese}
              synonyms={anime.title_synonyms}
            />

            <StatsGrid stats={statsConfig} />
          </div>
        </div>
      </MediaHero>

      {/* Main Content */}
      <div className="content-wrapper">
        <MediaTabs 
          tabs={tabs} 
          activeTab={activeTab} 
          onTabChange={handleTabChange} 
        />

        <Suspense fallback={<div className="tab-loading"><LoadingSpinner size="small" /></div>}>
          {activeTab === 'about' && <AnimeAbout anime={anime} />}
          {activeTab === 'trailer' && <AnimeTrailer trailer={anime.trailer} />}
          {activeTab === 'production' && <AnimeProduction anime={anime} />}
        </Suspense>
      </div>

      {/* Additional Information Footer */}
      <div className="additional-info">
        <span>Added: {formatDate(anime.created_at)}</span>
        <span>Last updated: {formatDate(anime.updated_at)}</span>
      </div>
    </div>
  );
};

// Wrap the component with Sentry's performance profiler
const AnimeDetails = Sentry.withProfiler(AnimeDetailsComponent, {
  name: "AnimeDetails",
});

export default React.memo(AnimeDetails);